import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MessageSquare, Users, Bookmark, Download, Trash2, Mail, MoreVertical } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AgentMessagesModal from '../components/AgentMessagesModal';

function ListingLine({ row }) {
  return (
    <li style={{ fontSize: 12, color: 'var(--text-secondary, #555)', marginBottom: 2 }}>
      {row.address_line1 || 'Property'}{row.city ? `, ${row.city}` : ''}
      {row.list_price != null && <> · ${Number(row.list_price).toLocaleString()}</>}
      {row.bedrooms != null && <> · {row.bedrooms} bd</>}
      {row.is_own_listing && <> · your listing</>}
    </li>
  );
}

function roleLabel(role) {
  if (role === 'both') return 'Buyer & seller';
  if (role === 'seller') return 'Seller';
  return 'Buyer';
}

function yesNo(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return null;
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ fontSize: 12, marginBottom: 3 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}: </span>
      <span style={{ color: 'var(--text-secondary, #555)' }}>{value}</span>
    </div>
  );
}

function BuyerDetails({ buyer }) {
  if (!buyer) return null;
  const sellFirst = yesNo(buyer.has_home_to_sell);
  const preApproved = yesNo(buyer.pre_approved);
  const firstTime = yesNo(buyer.first_time_buyer);
  const moveDirection = buyer.move_direction
    ? buyer.move_direction.charAt(0).toUpperCase() + buyer.move_direction.slice(1)
    : null;
  const hasAny = sellFirst != null || buyer.financing || preApproved != null
    || firstTime != null || moveDirection;
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Buyer details</div>
      <DetailRow label="First-time buyer" value={firstTime} />
      <DetailRow label="Needs to sell first" value={sellFirst} />
      <DetailRow label="Up/downsizing" value={moveDirection} />
      <DetailRow label="Financing" value={buyer.financing} />
      <DetailRow label="Pre-approved" value={preApproved} />
      {!hasAny && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not captured yet</div>}
    </div>
  );
}

function SellerDetails({ seller }) {
  if (!seller) return null;
  const bedsBaths = [
    seller.bedrooms != null ? `${seller.bedrooms} bd` : null,
    seller.bathrooms != null ? `${seller.bathrooms} ba` : null,
  ].filter(Boolean).join(' / ') || null;
  const hasAny = seller.property_type || bedsBaths || seller.approx_age
    || seller.condition || seller.features || seller.timeline;
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Property</div>
      <DetailRow label="Type" value={seller.property_type} />
      <DetailRow label="Beds / baths" value={bedsBaths} />
      <DetailRow label="Age / built" value={seller.approx_age} />
      <DetailRow label="Condition" value={seller.condition} />
      <DetailRow label="Features" value={seller.features} />
      <DetailRow label="Timeline" value={seller.timeline} />
      {!hasAny && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not captured yet</div>}
    </div>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openMessagesFor = searchParams.get('open');
  const focusClientId = searchParams.get('client');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [statesByClient, setStatesByClient] = useState({});
  const [messagingClient, setMessagingClient] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuId]);

  const clearUnread = useCallback((clientId) => {
    setClients((cs) => cs.map((x) => (x.agent_client_id === clientId ? { ...x, unread_messages: 0 } : x)));
  }, []);

  const toggleListings = async (client) => {
    const clientId = client.agent_client_id;
    if (expanded === clientId) {
      setExpanded(null);
      return;
    }
    setExpanded(clientId);
    if (client.client_role !== 'seller' && !statesByClient[clientId]) {
      setStatesByClient((s) => ({ ...s, [clientId]: { loading: true, saved: [], passed: [] } }));
      try {
        const res = await api.get(`/agent-clients/${clientId}/property-states`);
        setStatesByClient((s) => ({
          ...s,
          [clientId]: { loading: false, saved: res.data.saved || [], passed: res.data.passed || [] },
        }));
      } catch {
        setStatesByClient((s) => ({ ...s, [clientId]: { loading: false, saved: [], passed: [], error: true } }));
      }
    }
  };

  const fetchClients = () => {
    setLoading(true);
    const params = roleFilter ? `?role=${roleFilter}` : '';
    setLoadError('');
    api.get(`/agent-clients${params}`)
      .then((res) => setClients(res.data.clients || []))
      .catch(() => {
        setClients([]);
        setLoadError('Could not load clients.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClients();
  }, [roleFilter]);

  // Deep link from the dashboard's "Reply" action — jump straight to that client's thread.
  useEffect(() => {
    if (!openMessagesFor || !clients.length) return;
    const target = clients.find((c) => c.agent_client_id === openMessagesFor);
    if (target) setMessagingClient(target);
  }, [openMessagesFor, clients]);

  // Deep link from "View match" — expand that client's listings.
  useEffect(() => {
    if (!focusClientId || !clients.length) return;
    const target = clients.find((c) => c.agent_client_id === focusClientId);
    if (!target) return;
    setExpanded(focusClientId);
    if (target.client_role === 'seller') return;
    let cancelled = false;
    setStatesByClient((s) => {
      if (s[focusClientId] && !s[focusClientId].loading) return s;
      return { ...s, [focusClientId]: { loading: true, saved: [], passed: [] } };
    });
    api.get(`/agent-clients/${focusClientId}/property-states`)
      .then((res) => {
        if (cancelled) return;
        setStatesByClient((s) => ({
          ...s,
          [focusClientId]: { loading: false, saved: res.data.saved || [], passed: res.data.passed || [] },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setStatesByClient((s) => ({
          ...s,
          [focusClientId]: { loading: false, saved: [], passed: [], error: true },
        }));
      });
    return () => { cancelled = true; };
  }, [focusClientId, clients]);

  const filtered = clients.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return (
      name.includes(q)
      || c.email?.toLowerCase().includes(q)
      || c.phone?.includes(search)
      || c.profile_summary?.toLowerCase().includes(q)
    );
  });

  const downloadFile = (blobData, mimeType, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData], { type: mimeType }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSummary = async (c) => {
    try {
      const res = await api.get(`/agent-clients/${c.agent_client_id}/export-summary`, { responseType: 'blob' });
      const who = `${c.first_name || ''}${c.last_name || ''}`.trim() || 'client';
      downloadFile(res.data, 'text/plain', `${who}-summary.txt`);
    } catch {
      toast.error('Export failed');
    }
  };

  const handleExportRaw = async (c) => {
    try {
      const res = await api.get(`/agent-clients/${c.agent_client_id}/export`, { responseType: 'blob' });
      const who = `${c.first_name || ''}${c.last_name || ''}`.trim() || 'client';
      downloadFile(res.data, 'application/json', `${who}-${c.agent_client_id}.json`);
    } catch {
      toast.error('Export failed');
    }
  };

  const handleDelete = async (c) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'this client';
    if (!window.confirm(
      `Permanently delete ALL data for ${name}?\n\nThis removes their profile, conversations, saved searches, listing reactions, appointments, and account. This cannot be undone.`
    )) return;
    try {
      await api.delete(`/agent-clients/${c.agent_client_id}`);
      setClients((cs) => cs.filter((x) => x.agent_client_id !== c.agent_client_id));
      if (expanded === c.agent_client_id) setExpanded(null);
      toast.success(`Deleted all data for ${name}`);
    } catch {
      toast.error('Delete failed');
    }
  };

  const openConversation = (client) => {
    if (client.last_conversation_id) {
      const path = client.client_role === 'seller' ? 'seller' : 'buyer';
      navigate(`/chat?conv=${client.last_conversation_id}&path=${path}`);
      return;
    }
    navigate('/chat');
  };

  return (
    <div>
      <div className="page-header">
        <h2>Clients</h2>
        <p>
          Registered buyers and sellers linked to you — from saved search, seller consult, or portal signup.
          Anonymous chats appear under Conversations until they register.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
          <Search size={16} color="var(--text-muted)" />
          <input
            placeholder="Search clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="form-input"
          style={{ width: 'auto', padding: '8px 12px' }}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All roles</option>
          <option value="buyer">Buyers</option>
          <option value="seller">Sellers</option>
          <option value="both">Both</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : loadError ? (
          <div className="empty-state">
            <p>{loadError}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Users size={32} color="var(--text-muted)" />
            <p>No registered clients yet. They appear when someone saves a search or signs up after chatting.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Profile</th>
                  <th>Status</th>
                  <th>Last activity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <React.Fragment key={c.agent_client_id}>
                  <tr>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        {c.first_name} {c.last_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                    </td>
                    <td>
                      <span className={`badge badge-${c.client_role === 'seller' ? 'seller' : 'buyer'}`}>
                        {roleLabel(c.client_role)}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, maxWidth: 280 }}>
                      {c.profile_summary || '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {c.lifecycle_status ? (
                        <span className={`badge badge-${c.lifecycle_status}`}>
                          {c.lifecycle_status.replace('_', ' ')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 220 }}>
                      {c.last_conversation_at
                        ? new Date(c.last_conversation_at).toLocaleDateString()
                        : new Date(c.registered_at).toLocaleDateString()}
                      {c.conversation_count > 0 && (
                        <span style={{ color: 'var(--text-muted)' }}>
                          {' '}· {c.conversation_count} chat{c.conversation_count !== 1 ? 's' : ''}
                        </span>
                      )}
                      {c.last_conversation_summary && (
                        <div
                          style={{
                            color: 'var(--text-muted)',
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                          title={c.last_conversation_summary}
                        >
                          {c.last_conversation_summary}
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setMessagingClient(c)}
                        title="Direct messages with this client"
                        style={{ position: 'relative' }}
                      >
                        <Mail size={14} /> Messages
                        {c.unread_messages > 0 && (
                          <span
                            style={{
                              marginLeft: 5,
                              background: 'var(--danger, #e5484d)',
                              color: '#fff',
                              borderRadius: 10,
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '1px 6px',
                            }}
                          >
                            {c.unread_messages}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 4 }}
                        onClick={() => openConversation(c)}
                        title="Open in conversations inbox"
                      >
                        <MessageSquare size={14} /> Chat
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 4 }}
                        onClick={() => toggleListings(c)}
                        title="Profile details & listings"
                      >
                        <Bookmark size={14} /> {expanded === c.agent_client_id ? 'Hide' : 'Details'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 4 }}
                        onClick={() => handleExportSummary(c)}
                        title="Download a contact summary sheet"
                      >
                        <Download size={14} /> Export
                      </button>
                      <span
                        aria-hidden="true"
                        style={{ display: 'inline-block', width: 1, height: 18, background: 'var(--border)', margin: '0 8px', verticalAlign: 'middle' }}
                      />
                      <div style={{ position: 'relative', display: 'inline-block' }} ref={openMenuId === c.agent_client_id ? menuRef : undefined}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setOpenMenuId((id) => (id === c.agent_client_id ? null : c.agent_client_id))}
                          title="More actions"
                          aria-haspopup="true"
                          aria-expanded={openMenuId === c.agent_client_id}
                        >
                          <MoreVertical size={14} />
                        </button>
                        {openMenuId === c.agent_client_id && (
                          <div
                            role="menu"
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: 4,
                              background: 'var(--bg-card)',
                              border: '1px solid var(--border)',
                              borderRadius: 8,
                              boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.15))',
                              zIndex: 10,
                              minWidth: 140,
                            }}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              className="btn btn-ghost btn-sm"
                              style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 8 }}
                              onClick={() => { setOpenMenuId(null); handleExportRaw(c); }}
                              title="Export all raw data as JSON (PIPEDA access request)"
                            >
                              <Download size={14} /> Raw data (JSON)
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="btn btn-ghost btn-sm"
                              style={{ color: 'var(--danger)', width: '100%', justifyContent: 'flex-start', borderRadius: 8 }}
                              onClick={() => { setOpenMenuId(null); handleDelete(c); }}
                              title="Delete all of this client's data (PIPEDA)"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === c.agent_client_id && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--bg-hover, rgba(176,134,66,0.05))' }}>
                        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                          {c.client_role !== 'seller' && <BuyerDetails buyer={c.buyer} />}
                          {c.client_role !== 'buyer' && <SellerDetails seller={c.seller} />}
                          {c.client_role !== 'seller' && (
                            statesByClient[c.agent_client_id]?.loading ? (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading listings…</div>
                            ) : (
                              <>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                    Saved ({statesByClient[c.agent_client_id]?.saved?.length || 0})
                                  </div>
                                  {statesByClient[c.agent_client_id]?.saved?.length ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                      {statesByClient[c.agent_client_id].saved.map((row) => (
                                        <ListingLine key={row.property_id} row={row} />
                                      ))}
                                    </ul>
                                  ) : (
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>None yet</div>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                    Passed ({statesByClient[c.agent_client_id]?.passed?.length || 0})
                                  </div>
                                  {statesByClient[c.agent_client_id]?.passed?.length ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                      {statesByClient[c.agent_client_id].passed.map((row) => (
                                        <ListingLine key={row.property_id} row={row} />
                                      ))}
                                    </ul>
                                  ) : (
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>None yet</div>
                                  )}
                                </div>
                              </>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {messagingClient && (
        <AgentMessagesModal
          client={messagingClient}
          onClose={() => setMessagingClient(null)}
          onRead={clearUnread}
        />
      )}
    </div>
  );
}
